/**
 * AI Orchestration Surface & Controlled Execution — thin façade contracts V0.
 * Reuses SGEA + Controlled Business AI Orchestration + ControlledActionPort.
 * Not a second orchestration engine.
 */
import {
  isForbiddenAiOrchestrationTransitionV0,
  type AiOrchestrationProposalStatusV0,
  type AiOrchestrationProposalV0,
} from '../ai-orchestration/ControlledBusinessAiOrchestrationContract';
import type {
  PolicyDecisionStatusV0,
  RiskClassV0,
} from '../system-governed-execution-authority/contracts/SystemGovernedExecutionAuthorityContractsV0';

export const AI_ORCHESTRATION_SURFACE_CONTRACT_SCHEMA_VERSION =
  'ai-orchestration-surface-and-controlled-execution/v0' as const;

/** Master-command names → reuse disposition (P1 freeze). */
export type NameReuseDispositionV0 =
  | 'KEEP_EXISTING_NAME'
  | 'INTRODUCE_ALIAS'
  | 'REJECT_AS_DUPLICATE';

export const AI_NAME_REUSE_MAP_V0 = {
  AIActor: {
    disposition: 'KEEP_EXISTING_NAME' as const,
    ssot: 'PolicyActorV0.actorKind AI',
  },
  AIIdentity: {
    disposition: 'INTRODUCE_ALIAS' as const,
    ssot: 'PolicyActorV0.AI + AIDelegationReferenceV0',
  },
  AIContext: {
    disposition: 'INTRODUCE_ALIAS' as const,
    ssot: 'AiContextEnvelopeV0 façade',
  },
  AIObservation: {
    disposition: 'KEEP_EXISTING_NAME' as const,
    ssot: 'OBSERVE phase / AiShortageObservationV0 pattern',
  },
  AIInterpretation: {
    disposition: 'INTRODUCE_ALIAS' as const,
    ssot: 'WceIntentEnvelopeV0 confidence (≠ authority)',
  },
  AIProposal: {
    disposition: 'REJECT_AS_DUPLICATE' as const,
    ssot: 'AiOrchestrationProposalV0',
  },
  AIRecommendation: {
    disposition: 'KEEP_EXISTING_NAME' as const,
    ssot: 'AiAssistiveRecommendationProposal',
  },
  AIExecutionRequest: {
    disposition: 'INTRODUCE_ALIAS' as const,
    ssot: 'PolicyEvaluationInputV0 / BoundedActionRequestV0',
  },
  AIExecutionAttempt: {
    disposition: 'REJECT_AS_DUPLICATE' as const,
    ssot: 'idempotency/replay (no V0 entity)',
  },
  AIExecutionResult: {
    disposition: 'KEEP_EXISTING_NAME' as const,
    ssot: 'BoundedActionResultV0',
  },
  AIReceipt: {
    disposition: 'REJECT_AS_DUPLICATE' as const,
    ssot: 'DecisionReceiptV0 (projection only)',
  },
  AIRecovery: {
    disposition: 'KEEP_EXISTING_NAME' as const,
    ssot: 'WceExceptionRecordV0 / compensation',
  },
} as const;

export type ProvenanceClassV0 =
  | 'SOURCE_FACT'
  | 'DERIVED_INTERPRETATION'
  | 'ASSUMPTION'
  | 'PROPOSAL'
  | 'UNKNOWN'
  | 'CONFLICT';

export type AiSurfacePlaneV0 = 'platform' | 'public' | 'relationship' | 'business';

export type AiWorkbenchSlotV0 =
  | 'Descriptive'
  | 'Prescriptive'
  | 'Explanatory'
  | 'Predictive'
  | 'Denied';

export type AiSurfaceVerbV0 = 'RECOMMEND' | 'EXPLAIN' | 'PROPOSE' | 'DENY' | 'RECEIPT';

/** Context envelope — façade over existing contracts; not a second store. */
export type AiContextEnvelopeV0 = {
  tenantId: string;
  requestingActorId: string;
  requestingActorKind: 'HUMAN' | 'SYSTEM' | 'SERVICE' | 'AI';
  aiActorId?: string;
  purpose: string;
  threadId?: string;
  flowExecutionId?: string;
  taskId?: string;
  objectRefs: readonly string[];
  evidenceRefs: readonly string[];
  authoritySnapshotRef?: string;
  surfacePlane: AiSurfacePlaneV0;
  surfaceSlot?: AiWorkbenchSlotV0;
  piiClassification?: 'public' | 'internal' | 'confidential' | 'PII';
  redactionApplied: boolean;
  freshness: 'FRESH' | 'STALE' | 'UNKNOWN';
  contextTimestamp: string;
  excludedSources: readonly string[];
  correlationId?: string;
};

export const AI_CONTEXT_FORBIDDEN_KEYS_V0 = [
  'password',
  'apiKey',
  'api_key',
  'secret',
  'sessionToken',
  'accessToken',
  'refreshToken',
  'ssn',
  'taxId',
  'bankAccount',
  'hiddenReasoning',
  'chainOfThought',
  'rawModelScratchpad',
] as const;

export type AiSurfaceOutcomeV0 =
  | 'succeeded'
  | 'denied'
  | 'deferred'
  | 'insufficient'
  | 'failed-closed';

export const V0_CONTROLLED_ACTION_CATALOG_V0 = [
  'approval.approve',
  'approval.reject',
  'correction.approve',
  'correction.reject',
  'task.advance',
] as const;

export type V0ControlledActionKeyV0 = (typeof V0_CONTROLLED_ACTION_CATALOG_V0)[number];

export const DEFERRED_MUTATION_CLASSES_V0 = [
  'FINANCIAL_TRUTH_MUTATION',
  'INVENTORY_TRUTH_MUTATION',
  'LOGISTICS_DISPATCH_COMPLETION',
  'APPROVAL_AUTHORITY_ISSUANCE',
  'TASK_EXECUTION_COMPLETION',
  'PUBLISHED_OPERATION_COMPOSITION',
  'FCE_LIVE_SEND',
  'ASSISTIVE_AI_LIVE',
  'MULTI_STEP_AUTONOMOUS_AGENT',
] as const;

export type ProductAdmissionUxLabelV0 =
  | 'ADMITTED_FOR_PREVIEW'
  | 'ADMITTED_FOR_DRAFT'
  | 'ADMITTED_FOR_EXECUTION'
  | 'APPROVAL_REQUIRED'
  | 'MORE_EVIDENCE_REQUIRED'
  | 'DENIED'
  | 'EXPIRED'
  | 'SUPERSEDED';

/** Map product UX labels onto SGEA decisions + proposal status — no new SGEA enums. */
export function mapProductAdmissionUxToSystemV0(input: {
  decision: PolicyDecisionStatusV0;
  proposalStatus: AiOrchestrationProposalStatusV0;
  forExecution: boolean;
}): ProductAdmissionUxLabelV0 {
  if (input.decision === 'DENY' || input.decision === 'AUTHORITY_INSUFFICIENT') return 'DENIED';
  if (input.decision === 'INSUFFICIENT_EVIDENCE') return 'MORE_EVIDENCE_REQUIRED';
  if (
    input.decision === 'DEFER' ||
    input.decision === 'POLICY_UNRESOLVED' ||
    input.proposalStatus === 'PENDING'
  ) {
    return 'APPROVAL_REQUIRED';
  }
  if (input.decision === 'ALLOW' && input.proposalStatus === 'APPROVED' && input.forExecution) {
    return 'ADMITTED_FOR_EXECUTION';
  }
  if (input.decision === 'ALLOW' && !input.forExecution) {
    return 'ADMITTED_FOR_PREVIEW';
  }
  return 'APPROVAL_REQUIRED';
}

export function mapPolicyDecisionToSurfaceOutcomeV0(
  decision: PolicyDecisionStatusV0
): AiSurfaceOutcomeV0 {
  switch (decision) {
    case 'ALLOW':
      return 'succeeded';
    case 'DENY':
      return 'denied';
    case 'DEFER':
      return 'deferred';
    case 'INSUFFICIENT_EVIDENCE':
    case 'AUTHORITY_INSUFFICIENT':
    case 'POLICY_UNRESOLVED':
      return 'insufficient';
    default:
      return 'failed-closed';
  }
}

export function assertNoForbiddenContextKeysV0(
  record: Record<string, unknown>
): { ok: true } | { ok: false; leaked: string[] } {
  const leaked = AI_CONTEXT_FORBIDDEN_KEYS_V0.filter((k) =>
    Object.prototype.hasOwnProperty.call(record, k)
  );
  return leaked.length === 0 ? { ok: true } : { ok: false, leaked: [...leaked] };
}

export function isV0ControlledActionKeyV0(key: string): key is V0ControlledActionKeyV0 {
  return (V0_CONTROLLED_ACTION_CATALOG_V0 as readonly string[]).includes(key);
}

export function canAiInvokeControlledExecutionV0(input: {
  actorKind: 'HUMAN' | 'SYSTEM' | 'SERVICE' | 'AI';
  sgeaDecision: PolicyDecisionStatusV0;
  proposalStatus: AiOrchestrationProposalStatusV0;
  assistiveAiLiveEnabled?: boolean;
}): { allowed: boolean; reason?: string } {
  if (input.assistiveAiLiveEnabled) {
    return { allowed: false, reason: 'ASSISTIVE_AI_LIVE_MUST_REMAIN_OFF' };
  }
  if (input.actorKind === 'AI') {
    return { allowed: false, reason: 'AI_DIRECT_EXECUTE_DENIED' };
  }
  if (input.proposalStatus === 'PENDING') {
    return { allowed: false, reason: 'PENDING_REQUIRES_OPERATOR_RESOLVE' };
  }
  if (
    isForbiddenAiOrchestrationTransitionV0({
      from: 'PENDING',
      to: 'EXECUTED',
      assistiveAiLiveEnabled: input.assistiveAiLiveEnabled,
    })
  ) {
    /* keep live-flag path covered even when status already advanced */
  }
  if (input.sgeaDecision !== 'ALLOW') {
    return { allowed: false, reason: `SGEA_${input.sgeaDecision}` };
  }
  if (input.proposalStatus !== 'APPROVED') {
    return { allowed: false, reason: 'OPERATOR_APPROVAL_REQUIRED' };
  }
  return { allowed: true };
}

export function assembleWorkbenchNextActionContextV0(input: {
  tenantId: string;
  requestingActorId: string;
  taskId: string;
  evidenceRefs: readonly string[];
  correlationId?: string;
}): AiContextEnvelopeV0 {
  return {
    tenantId: input.tenantId,
    requestingActorId: input.requestingActorId,
    requestingActorKind: 'HUMAN',
    purpose: 'workbench_task_next_action_analysis',
    taskId: input.taskId,
    objectRefs: [],
    evidenceRefs: input.evidenceRefs,
    surfacePlane: 'business',
    surfaceSlot: 'Prescriptive',
    redactionApplied: true,
    freshness: 'FRESH',
    contextTimestamp: new Date(0).toISOString(),
    excludedSources: ['hiddenReasoning', 'crossTenant', 'secrets'],
    correlationId: input.correlationId,
  };
}

export function buildPreviewProposalReceiptProjectionV0(input: {
  proposal: AiOrchestrationProposalV0;
  riskClass: RiskClassV0;
  sgeaDecision: PolicyDecisionStatusV0;
  decisionReceiptId?: string;
  provenance: ProvenanceClassV0;
}): {
  proposalId: string;
  status: AiOrchestrationProposalStatusV0;
  riskClass: RiskClassV0;
  sgeaDecision: PolicyDecisionStatusV0;
  uxAdmission: ProductAdmissionUxLabelV0;
  decisionReceiptId?: string;
  provenance: ProvenanceClassV0;
  grantsAuthority: false;
  businessMutationDelta: 0;
} {
  return {
    proposalId: input.proposal.proposalId,
    status: input.proposal.status,
    riskClass: input.riskClass,
    sgeaDecision: input.sgeaDecision,
    uxAdmission: mapProductAdmissionUxToSystemV0({
      decision: input.sgeaDecision,
      proposalStatus: input.proposal.status,
      forExecution: false,
    }),
    decisionReceiptId: input.decisionReceiptId,
    provenance: input.provenance,
    grantsAuthority: false,
    businessMutationDelta: 0,
  };
}

export const DESIGN_GATE_PASS_REQUIREMENTS_V0 = [
  'no_parallel_business_truth',
  'no_direct_AI_database_mutation',
  'no_authority_inherited_implicitly',
  'tenant_isolation_defined',
  'acting_for_evidence_defined',
  'proposal_admission_separation_defined',
  'executor_boundary_defined',
  'receipt_source_defined',
  'prompt_injection_boundary_defined',
  'pii_redaction_defined',
  'v0_proof_lane_feasible',
] as const;

export const SURFACE_GRAMMAR_VERBS_V0: readonly AiSurfaceVerbV0[] = [
  'RECOMMEND',
  'EXPLAIN',
  'PROPOSE',
  'DENY',
  'RECEIPT',
] as const;
